import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Pagination, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import {
  fetchProjectServers,
  nginxDeleteSubdomain,
  nginxListSubdomains,
  nginxProvisionSsl,
  nginxSplitSubdomain,
  nginxToggleSubdomain,
} from "../../redux/actionCreator";
import AddSubdomainModal from "../Nginx/AddSubdomainModal";
import WildcardCertModal from "./WildcardCertModal";
import ExposeComposeModal from "./ExposeComposeModal";
import EditConfigModal from "./EditConfigModal";
import {
  Check, X, AlertTriangle, Lock, Pencil, Scissors, Play, Pause,
} from "lucide-react";
import "./subdomainsPage.scss";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Full-page Subdomains manager — one server at a time. Lifts the cPanel-style
 * subdomain list out of the NGINX modal so it can paginate, filter, and stay
 * focused on subdomain lifecycle (add / SSL / disable / split / delete) rather
 * than raw config editing (which lives in NGINX → Configs).
 *
 * Per-row Edit is intentionally absent — anything that would regenerate the
 * vhost from a template is gated behind Split first so we never clobber
 * hand-written multi-block files. Delete works in both modes: file-level for
 * single-subdomain files, block-carve for shared files.
 */
export default function SubdomainsPage() {
  const dispatch = useDispatch();
  const { projectId, serverId } = useParams();
  const [searchParams] = useSearchParams();
  const labelHint = searchParams.get("label");

  const [server, setServer] = useState(null);

  // Every server on the project — a jumphost vhost usually proxies to one

  // of the OTHER ones, so the picker needs the whole list, not just this.

  const [servers, setServers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [showAdd, setShowAdd] = useState(false);
  const [showWildcard, setShowWildcard] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [sslOutput, setSslOutput] = useState(null);
  const [splitting, setSplitting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);

  const rowKey = (r) => `${r.path}::${r.primary_fqdn}`;

  // Load the server label/host so the page header has context. We hit
  // fetchProjectServers because the project list already strips secrets.
  useEffect(() => {
    if (!projectId || !serverId) return;
    dispatch(fetchProjectServers(projectId)).then((res) => {
      if (res?.status) {
        setServers(res.data || []);
        setServer((res.data || []).find((s) => String(s._id) === String(serverId)) || null);
      }
    });
  }, [dispatch, projectId, serverId]);

  const load = useCallback(async () => {
    if (!projectId || !serverId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(nginxListSubdomains(projectId, serverId));
    setLoading(false);
    if (res?.status) setRows(res.data || []);
    else setErr(res?.message || "Failed to list subdomains");
  }, [dispatch, projectId, serverId]);

  useEffect(() => {
    load();
  }, [load]);

  // Filter rows by primary FQDN OR any alias (substring, case-insensitive).
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      if (r.primary_fqdn?.toLowerCase().includes(q)) return true;
      if (r.upstream?.toLowerCase().includes(q)) return true;
      if (r.filename?.toLowerCase().includes(q)) return true;
      if (r.fqdns?.some((f) => f.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [rows, filter]);

  // Recompute pagination whenever filter or pageSize shifts.
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const provisionSsl = async (row) => {
    const fqdn = row.primary_fqdn;
    if (!fqdn || fqdn.startsWith("(")) {
      alert("Couldn't parse a FQDN for this row — open the raw file and run SSL by hand.");
      return;
    }
    if (
      !window.confirm(
        `Run certbot for ${fqdn}? Calls --dns-cloudflare and rewrites the vhost to HTTPS on success.`
      )
    )
      return;
    setBusyKey(rowKey(row));
    setSslOutput(null);
    const res = await dispatch(
      nginxProvisionSsl(projectId, serverId, { fqdn, rewrite_vhost: true })
    );
    setBusyKey(null);
    if (res?.status) {
      setSslOutput({ ok: true, fqdn, text: res?.data?.output || "Done." });
      load();
    } else {
      setSslOutput({
        ok: false,
        fqdn,
        text: `${res?.message || "certbot failed"}\n\n${res?.data?.output || ""}`,
      });
    }
  };

  const toggle = async (row) => {
    if (row.multi_block) {
      alert("This file holds multiple subdomains — disabling would rename the whole file. Split this one out first.");
      return;
    }
    const action = row.disabled ? "enable" : "disable";
    setBusyKey(rowKey(row));
    const res = await dispatch(
      nginxToggleSubdomain(projectId, serverId, row.filename, action, { path: row.path })
    );
    setBusyKey(null);
    if (res?.status) load();
    else alert(res?.message || `${action} failed`);
  };

  const openSplit = (row) => {
    const fqdn = row.primary_fqdn;
    if (!fqdn || fqdn.startsWith("(")) {
      alert("Couldn't parse a FQDN to anchor the split on.");
      return;
    }
    setSplitting({ row, busy: false, target: `${fqdn}.conf`, err: null });
  };

  const runSplit = async () => {
    if (!splitting || splitting.busy) return;
    setSplitting((s) => ({ ...s, busy: true, err: null }));
    const res = await dispatch(
      nginxSplitSubdomain(projectId, serverId, {
        source_path: splitting.row.path,
        primary_fqdn: splitting.row.primary_fqdn,
        target_filename: splitting.target,
      })
    );
    if (res?.status) {
      setSplitting(null);
      load();
    } else {
      setSplitting((s) => ({
        ...s,
        busy: false,
        err: `${res?.message || "Split failed"}\n\n${res?.data?.test_output || ""}`.trim(),
      }));
    }
  };

  const askDelete = (row) => setConfirmDelete({ row, busy: false, err: null });

  const runDelete = async () => {
    if (!confirmDelete || confirmDelete.busy) return;
    const row = confirmDelete.row;
    setConfirmDelete((c) => ({ ...c, busy: true, err: null }));
    // Block-carve when the file holds more than one subdomain; whole-file rm
    // when this row IS the file.
    const scope = row.multi_block
      ? { path: row.path, primary_fqdn: row.primary_fqdn }
      : { path: row.path };
    const res = await dispatch(
      nginxDeleteSubdomain(projectId, serverId, row.filename, scope)
    );
    if (res?.status) {
      setConfirmDelete(null);
      load();
    } else {
      setConfirmDelete((c) => ({
        ...c,
        busy: false,
        err: `${res?.message || "Delete failed"}\n\n${res?.data?.test_output || ""}`.trim(),
      }));
    }
  };

  const pageNumbers = useMemo(() => {
    // Compact pager: first, last, current ±2, ellipsis fillers.
    const out = [];
    const push = (n) => { if (!out.includes(n) && n >= 1 && n <= totalPages) out.push(n); };
    push(1);
    for (let i = page - 2; i <= page + 2; i++) push(i);
    push(totalPages);
    out.sort((a, b) => a - b);
    const withGaps = [];
    for (let i = 0; i < out.length; i++) {
      if (i > 0 && out[i] - out[i - 1] > 1) withGaps.push("…");
      withGaps.push(out[i]);
    }
    return withGaps;
  }, [page, totalPages]);

  return (
    <div className="subdomains-page">
      <div className="subdomains-header">
        <div>
          <h3 className="m-0">Subdomains</h3>
          <div className="text-muted small">
            {labelHint || server?.label}
            {server?.host && (
              <> · <code>{server.username}@{server.host}</code></>
            )}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="light" size="sm" onClick={load} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : "↻ Refresh"}
          </Button>
          <Button variant="light" size="sm" onClick={() => setShowCompose(true)}
            title="Create vhosts from the services a compose project runs">
            From compose
          </Button>
          <Button variant="light" size="sm" onClick={() => setShowWildcard(true)}
            title="Issue one *.domain certificate covering every subdomain">
            Wildcard cert
          </Button>
          <Button variant="primary" size="sm" onClick={() => setShowAdd(true)}>
            ＋ Add Subdomain
          </Button>
        </div>
      </div>

      <div className="subdomains-toolbar">
        <Form.Control
          size="sm"
          type="search"
          placeholder="Filter by FQDN, alias, upstream, or filename…"
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(1); }}
          style={{ maxWidth: 360 }}
        />
        <span className="ms-auto text-muted small">
          {filtered.length} {filtered.length === 1 ? "match" : "matches"}
          {rows.length !== filtered.length && <> · of {rows.length}</>}
        </span>
        <Form.Select
          size="sm"
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          style={{ width: 110 }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </Form.Select>
      </div>

      {sslOutput && (
        <div className={`alert ${sslOutput.ok ? "alert-success" : "alert-danger"}`}>
          <div className="d-flex justify-content-between">
            <strong className="d-inline-flex align-items-center gap-2">
              {sslOutput.ok ? <Check size={14} /> : <X size={14} />} {sslOutput.fqdn}
            </strong>
            <button type="button" className="btn-close" onClick={() => setSslOutput(null)} />
          </div>
          <pre style={{ fontSize: 11, maxHeight: 200, overflow: "auto", margin: "6px 0 0", whiteSpace: "pre-wrap" }}>
            {sslOutput.text}
          </pre>
        </div>
      )}

      {err && <div className="alert alert-danger">{err}</div>}

      <div className="subdomains-table-wrap">
        <table className="subdomains-table">
          <thead>
            <tr>
              <th>Subdomain</th>
              <th>Upstream</th>
              <th>File</th>
              <th>SSL</th>
              <th>State</th>
              <th className="text-end" style={{ width: 1 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="empty"><Spinner size="sm" animation="border" /> Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty">
                  {rows.length === 0
                    ? "No subdomains yet. Click ＋ Add Subdomain to create one."
                    : "No matches for that filter."}
                </td>
              </tr>
            ) : (
              pageRows.map((r) => {
                const k = rowKey(r);
                const busy = busyKey === k;
                return (
                  <tr key={k}>
                    <td>
                      <div className="fqdn">{r.primary_fqdn}</div>
                      {r.fqdns?.length > 1 && (
                        <div className="aliases">
                          + aliases: {r.fqdns.slice(1).join(", ")}
                        </div>
                      )}
                    </td>
                    <td><code className="small">{r.upstream || "—"}</code></td>
                    <td>
                      <code className="small">{r.filename}</code>
                      {r.dir && (
                        <div className="dirhint" title={r.dir}>
                          in <code>{r.dir.replace(/^.*\//, "")}</code>
                        </div>
                      )}
                      {r.multi_block && (
                        <div className="shared-hint" title={`This file has ${r.groups_in_file} subdomains.`}>
                          <span className="d-inline-flex align-items-center gap-1"><AlertTriangle size={12} /> shared file ({r.groups_in_file} vhosts)</span>
                        </div>
                      )}
                    </td>
                    <td>
                      {r.ssl
                        ? <span className="badge bg-success d-inline-flex align-items-center gap-1"><Lock size={10} /> enabled</span>
                        : <span className="badge bg-secondary">plain</span>}
                    </td>
                    <td>
                      {r.disabled
                        ? <span className="badge bg-warning text-dark">disabled</span>
                        : <span className="badge bg-success">active</span>}
                    </td>
                    <td className="text-end actions">
                      <Button
                        size="sm"
                        variant="light"
                        onClick={() => setEditingConfig(r)}
                        disabled={busy || r.primary_fqdn?.startsWith("(")}
                        title="View and edit just this subdomain's server { } blocks"
                      >
                        <Pencil size={12} className="me-1" /> Edit config
                      </Button>
                      {r.multi_block && (
                        <Button size="sm" variant="light" onClick={() => openSplit(r)} disabled={busy} title="Move this subdomain into its own file">
                          <Scissors size={12} className="me-1" /> Split
                        </Button>
                      )}
                      <Button size="sm" variant="light" onClick={() => provisionSsl(r)} disabled={busy} title={r.ssl ? "Re-run certbot" : "Issue a Let's Encrypt cert"}>
                        {busy ? <Spinner size="sm" animation="border" /> : <span className="d-inline-flex align-items-center gap-1"><Lock size={12} /> {r.ssl ? "Renew" : "SSL"}</span>}
                      </Button>
                      <Button
                        size="sm"
                        variant="light"
                        onClick={() => toggle(r)}
                        disabled={busy || r.multi_block}
                        title={r.multi_block ? "Disabling renames the whole file — Split first." : (r.disabled ? "Re-enable" : "Disable without deleting")}
                      >
                        {r.disabled
                          ? <span className="d-inline-flex align-items-center gap-1"><Play size={12} /> Enable</span>
                          : <span className="d-inline-flex align-items-center gap-1"><Pause size={12} /> Disable</span>}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => askDelete(r)}
                        disabled={busy}
                        title={r.multi_block
                          ? "Carves this server block out of the shared file"
                          : "Removes the whole vhost file"}
                      >
                        <X size={12} className="me-1" /> Delete
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="subdomains-pager">
          <Pagination size="sm" className="m-0">
            <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
            <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
            {pageNumbers.map((n, i) =>
              n === "…" ? (
                <Pagination.Ellipsis key={`e${i}`} disabled />
              ) : (
                <Pagination.Item key={n} active={n === page} onClick={() => setPage(n)}>
                  {n}
                </Pagination.Item>
              )
            )}
            <Pagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)} />
            <Pagination.Last disabled={page === totalPages} onClick={() => setPage(totalPages)} />
          </Pagination>
          <span className="text-muted small ms-2">
            Page {page} of {totalPages}
          </span>
        </div>
      )}

      <AddSubdomainModal
        servers={servers}
        show={showAdd}
        onHide={() => setShowAdd(false)}
        project={{ _id: projectId }}
        server={server || { _id: serverId }}
        onDone={() => { setShowAdd(false); load(); }}
      />

      <ExposeComposeModal
        show={showCompose}
        onHide={() => setShowCompose(false)}
        projectId={projectId}
        serverId={serverId}
        onDone={load}
      />

      <WildcardCertModal
        show={showWildcard}
        onHide={() => setShowWildcard(false)}
        projectId={projectId}
        serverId={serverId}
        onDone={load}
      />

      <EditConfigModal
        show={!!editingConfig}
        onHide={() => setEditingConfig(null)}
        projectId={projectId}
        serverId={serverId}
        row={editingConfig}
        onSaved={() => load()}
      />

      <Modal
        show={!!splitting}
        onHide={() => !splitting?.busy && setSplitting(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!splitting?.busy}>
          <h5 className="m-0">Split {splitting?.row?.primary_fqdn}</h5>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted">
            Moves every <code>server</code> block in{" "}
            <code className="small">{splitting?.row?.filename}</code> with{" "}
            <strong>server_name {splitting?.row?.primary_fqdn}</strong> into a new file.
            The source keeps the other vhosts. nginx -t runs before commit; both
            files roll back on failure.
          </p>
          <label className="form-label small">New filename</label>
          <Form.Control
            type="text"
            style={{ fontFamily: "ui-monospace, monospace" }}
            value={splitting?.target || ""}
            disabled={splitting?.busy}
            onChange={(e) =>
              setSplitting((s) => (s ? { ...s, target: e.target.value } : s))
            }
          />
          <small className="text-muted d-block mt-1">
            Will be created in <code>{splitting?.row?.dir}</code>.
          </small>
          {splitting?.err && (
            <pre className="alert alert-danger mt-3" style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
              {splitting.err}
            </pre>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setSplitting(null)} disabled={splitting?.busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={runSplit} disabled={splitting?.busy || !splitting?.target}>
            {splitting?.busy ? <Spinner size="sm" animation="border" /> : "Split"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!confirmDelete}
        onHide={() => !confirmDelete?.busy && setConfirmDelete(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!confirmDelete?.busy}>
          <h5 className="m-0">Delete {confirmDelete?.row?.primary_fqdn}?</h5>
        </Modal.Header>
        <Modal.Body>
          {confirmDelete?.row?.multi_block ? (
            <p className="small">
              This will carve every <code>server</code> block with{" "}
              <strong>server_name {confirmDelete?.row?.primary_fqdn}</strong> out of{" "}
              <code className="small">{confirmDelete?.row?.filename}</code> and discard them.
              The other vhosts in that file are kept untouched.
            </p>
          ) : (
            <p className="small">
              This will remove the file{" "}
              <code className="small">{confirmDelete?.row?.path}</code> entirely
              and reload nginx.
            </p>
          )}
          <p className="small text-muted">nginx -t runs before commit; the source is rolled back if it fails.</p>
          {confirmDelete?.err && (
            <pre className="alert alert-danger mt-2" style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
              {confirmDelete.err}
            </pre>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setConfirmDelete(null)} disabled={confirmDelete?.busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={runDelete} disabled={confirmDelete?.busy}>
            {confirmDelete?.busy ? <Spinner size="sm" animation="border" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
